<!--
Copyright 2020 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <div id="form-head">
    <div id="form-head-project-nav" class="row">
      <div class="col-xs-12">
        <div v-if="project !=null">
          <span>
            <router-link :to="projectPath()">
              {{ project.nameWithArchived() }}</router-link>
          </span>
          <router-link :to="projectPath()">{{ $t('projectNav.action.back') }}</router-link>
        </div>
        <!-- The triangle below the project name -->
        <div></div>
      </div>
    </div>
    <div id="form-head-form-nav" class="row">
      <div class="col-xs-12">
        <div class="row">
          <!-- Using .col-xs-6 so that if the form name is long, it is not
          behind #form-head-draft-nav. -->
          <div class="col-xs-6">
            <div v-if="form != null" class="h1" :title="form.nameOrId()">
              {{ form.nameOrId() }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-xs-6">
            <ul id="form-head-form-tabs" class="nav nav-tabs">
              <!-- Using rendersFormTabs rather than canRoute(), because we want
              to render the tabs even if the form does not have a published
              version (in which case canRoute() will return `false`). -->
              <li v-if="rendersFormTabs" :class="formTabClass('')"
                :title="formTabTitle" role="presentation">
                <router-link :to="tabPath('')">
                  {{ $t('formNav.tab.overview') }}
                </router-link>
              </li>
              <!-- No v-if, because anyone who can navigate to the form should
              be able to navigate to .../versions and .../submissions. -->
              <li :class="formTabClass('versions')" :title="formTabTitle"
                role="presentation">
                <router-link :to="tabPath('versions')">
                  {{ $t('formNav.tab.versions') }}
                </router-link>
              </li>
              <li :class="formTabClass('submissions')" :title="formTabTitle"
                role="presentation">
                <router-link :to="tabPath('submissions')">
                  {{ $t('resource.submissions') }}
                </router-link>
              </li>
              <li v-if="rendersFormTabs" :class="formTabClass('public-links')"
                :title="formTabTitle" role="presentation">
                <router-link :to="tabPath('public-links')">
                  {{ $t('formNav.tab.publicLinks') }}
                </router-link>
              </li>
              <li v-if="rendersFormTabs" :class="formTabClass('settings')"
                :title="formTabTitle" role="presentation">
                <router-link :to="tabPath('settings')">
                  {{ $t('formNav.tab.settings') }}
                </router-link>
              </li>
            </ul>
          </div>
          <div v-if="rendersDraftNav" id="form-head-draft-nav"
            class="col-xs-6" :class="{ 'draft-exists': formDraft.isDefined() }">
            <span id="form-head-draft-nav-title">{{ $t('draftNav.title') }}</span>
            <button v-show="formDraft.isEmpty()"
              id="form-head-create-draft-button" type="primary"
              class="btn btn-primary" @click="$emit('create-draft')">
              <span class="icon-plus-circle"></span>{{ $t('draftNav.action.create') }}
            </button>
            <ul v-show="formDraft.isDefined()" class="nav nav-tabs">
              <li v-if="canRoute(tabPath('draft'))" :class="tabClass('draft')"
                role="presentation">
                <router-link :to="tabPath('draft')">
                  {{ $t('draftNav.tab.status') }}
                </router-link>
              </li>
              <li v-if="canRoute(tabPath('draft/attachments'))"
                :class="tabClass('draft/attachments')" role="presentation">
                <router-link :to="tabPath('draft/attachments')">
                  {{ $t('draftNav.tab.attachments') }}
                  <template v-if="attachments != null">
                    <span v-show="missingAttachmentCount !== 0" class="badge">
                      {{ $n(missingAttachmentCount, 'default') }}
                    </span>
                  </template>
                </router-link>
              </li>
              <li v-if="canRoute(tabPath('draft/testing'))"
                :class="tabClass('draft/testing')" role="presentation">
                <router-link :to="tabPath('draft/testing')">
                  {{ $t('draftNav.tab.testing') }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import routes from '../../mixins/routes';
import tab from '../../mixins/tab';
import { requestData } from '../../store/modules/request';

const requestKeys = ['project', 'form', 'formDraft', 'attachments'];

export default {
  name: 'FormHead',
  mixins: [routes(), tab()],
  computed: {
    // The component does not assume that this data will exist when the
    // component is created.
    ...requestData(requestKeys),
    ...mapGetters(['missingAttachmentCount']),
    dataExists() {
      return this.$store.getters.dataExists(requestKeys);
    },
    tabPathPrefix() {
      return this.formPath();
    },
    rendersFormTabs() {
      return this.project != null && this.project.permits(['form.update']);
    },
    formTabTitle() {
      return this.form != null && this.form.publishedAt == null
        ? this.$t('formNav.tabTitle')
        : null;
    },
    rendersDraftNav() {
      return this.dataExists &&
        (this.formDraft.isDefined() || this.project.permits('form.update'));
    }
  },
  methods: {
    formTabClass(path) {
      const htmlClass = this.tabClass(path);
      if (this.form != null && this.form.publishedAt == null)
        htmlClass.disabled = true;
      return htmlClass;
    }
  }
};
</script>

<style lang="scss">
@import '../../assets/scss/variables';

$draft-nav-padding: 23px;
$tab-li-margin-top: 5px;

body {
  // If as the user navigates between the tabs, the scrollbar is visible for
  // only some tabs, then the position of the tabs will shift as the user
  // navigates. To prevent that, we always show the scrollbar.
  overflow-y: scroll;
}

#form-head-project-nav, #form-head-form-nav {
  background-color: $color-subpanel-background;
}

#form-head-project-nav > .col-xs-12 > div:first-child {
  background-color: #ddd;
  font-size: 18px;
  margin: 0 -15px;
  padding: 15px;

  > span {
    font-weight: bold;
    margin-right: 10px;

    a {
      color: inherit;
      text-decoration: none;
    }
  }

  > a {
    font-size: 12px;
  }

  + div {
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 12px solid #ddd;
    height: 0;
    margin-bottom: -10px;
    width: 0;
  }
}

#form-head-form-nav {
  border-bottom: 1px solid $color-subpanel-border-strong;

  .h1 {
    margin-bottom: -10px;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .nav-tabs > li {
    // It might be simpler to move this margin to the .nav-tabs element so that
    // fewer elements have margin.
    margin-top: $tab-li-margin-top;
  }
}

#form-head-form-tabs {
  margin-top: $draft-nav-padding;

  > li.active > a {
    &, &:hover, &:focus {
      background-color: $color-subpanel-active;
    }
  }
}

#form-head-draft-nav {
  background-color: #ddd;
  padding-top: $draft-nav-padding;
  position: relative;

  #form-head-draft-nav-title {
    color: #666;
    font-size: 12px;
    position: absolute;
    top: 7px;
  }
  &.draft-exists #form-head-draft-nav-title {
    left: /* .col-xs-6 padding-left */ 15px +
      /* .nav-tabs > li > a padding-left */ 8px;
  }

  #form-head-create-draft-button {
    /*
    6px =   1px (.nav-tabs > li > a has more top padding than .btn)
          + 1px (.nav-tabs > li > a has more bottom padding)
          + 1px (.nav-tabs > li > a has a bottom border)
          + 3px (because .nav-tabs > li > a has a higher font size?)
    */
    margin-bottom: 6px;
    margin-top: $tab-li-margin-top;
  }

  .nav-tabs > li.active > a {
    &, &:hover, &:focus {
      background-color: $color-page-background;
    }
  }
}
</style>

<i18n lang="json5">
{
  "en": {
    "projectNav": {
      "action": {
        "back": "Back to Project Overview"
      }
    },
    "formNav": {
      "tab": {
        "overview": "Overview",
        "versions": "Versions",
        "publicLinks": "Public Access",
        "settings": "Settings"
      },
      // Tooltip text that will be shown when hovering over tabs for Form Overview, Submissions, etc.
      "tabTitle": "These functions will become available once you publish your Draft Form"
    },
    "draftNav": {
      // This is shown above the navigation tabs for the Form Draft.
      "title": "Draft",
      "action": {
        "create": "Create a new Draft"
      },
      "tab": {
        "status": "Status",
        "attachments": "Media Files",
        "testing": "Testing"
      }
    }
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "projectNav": {
      "action": {
        "back": "Zpět na přehled projektu"
      }
    },
    "formNav": {
      "tab": {
        "overview": "Přehled",
        "versions": "Verze",
        "publicLinks": "Veřejný přístup",
        "settings": "Nastavení"
      },
      "tabTitle": "Tyto funkce budou k dispozici, jakmile zveřejníte svůj koncept formuláře"
    },
    "draftNav": {
      "title": "Koncept",
      "action": {
        "create": "Vytvořit nový koncept"
      },
      "tab": {
        "status": "Stav",
        "attachments": "Mediální soubory",
        "testing": "Testování"
      }
    }
  },
  "de": {
    "projectNav": {
      "action": {
        "back": "Zurück zur Projektübersicht"
      }
    },
    "formNav": {
      "tab": {
        "overview": "Übersicht",
        "versions": "Versionen",
        "publicLinks": "Öffentlicher Zugriff",
        "settings": "Einstellungen"
      },
      "tabTitle": "Diese Funktionen stehen zur Verfügung, wenn Sie Ihren Entwurf veröffentlicht haben."
    },
    "draftNav": {
      "title": "Entwurf",
      "action": {
        "create": "Neuen Entwurf erstellen"
      },
      "tab": {
        "status": "Status",
        "attachments": "Mediendateien",
        "testing": "Testen"
      }
    }
  },
  "es": {
    "projectNav": {
      "action": {
        "back": "Volver a la descripción general del proyecto."
      }
    },
    "formNav": {
      "tab": {
        "overview": "Descripción general",
        "versions": "Versiones",
        "publicLinks": "Acceso público",
        "settings": "Ajustes"
      },
      "tabTitle": "Estas funciones estarán disponibles una vez que publique su borrador de formulario."
    },
    "draftNav": {
      "title": "Borrador",
      "action": {
        "create": "Crear un nuevo borrador"
      },
      "tab": {
        "status": "Estado",
        "attachments": "Archivos multimedia",
        "testing": "Pruebas"
      }
    }
  },
  "fr": {
    "projectNav": {
      "action": {
        "back": "Retourner à l'aperçu du projet"
      }
    },
    "formNav": {
      "tab": {
        "overview": "Aperçu",
        "versions": "Versions",
        "publicLinks": "Accès public",
        "settings": "Réglages"
      },
      "tabTitle": "Ces fonctions seront disponibles quand vous publierez votre ébauche"
    },
    "draftNav": {
      "title": "Ébauche",
      "action": {
        "create": "Créer une nouvelle ébauche"
      },
      "tab": {
        "status": "Status",
        "attachments": "Fichiers médias",
        "testing": "Test"
      }
    }
  }
}
</i18n>
